import { Box, Flex, Grid, Heading, Text } from "@radix-ui/themes";
import { Fragment } from "react";
import { HomeConsultForm } from "~/components/home/HomeConsultForm";
import {
	Locale,
	useLocalization,
} from "~/components/localization/LocalizationProvider";
import { Link } from "~/components/RouterLink";
import styles from "../live-seo-page.module.css";

type MiamiOrthodonticsLocalization = {
	heroTitle: string;
	introBeforePractice: string;
	practiceLink: string;
	introAfterPractice: string;
	agesLink: string;
	introAfterAges: string;
	cityLink: string;
	introAfterCity: string;
	addressLabel: string;
	addressLine1: string;
	addressLine2: string;
	phoneLabel: string;
	phone: string;
	hoursLabel: string;
	hours: string[];
	bracesHeading: string;
	bracesParagraphs: string[];
	invisalignHeading: string;
	invisalignParagraphs: string[];
	treatmentHeading: string;
	treatmentParagraphs: string[];
	finalPrefix: string;
	finalLink: string;
	finalSuffix: string;
};

const miamiOrthodonticsLocalization = {
	[Locale.EN]: {
		heroTitle: "Miami Orthodontics",
		introBeforePractice: "At ",
		practiceLink: "Miami Orthodontic Specialists",
		introAfterPractice:
			", high-quality orthodontics is our top priority, and we are proud to produce beautifully aligned smiles for patients of ",
		agesLink: "all ages",
		introAfterAges: " in ",
		cityLink: "Miami",
		introAfterCity:
			", and the surrounding communities. Miami Orthodontic Specialists is your Miami orthodontics expert!",
		addressLabel: "Address:",
		addressLine1: "8525 SW 92nd Street, Suite B-9",
		addressLine2: "Miami, FL 33156",
		phoneLabel: "Phone:",
		phone: "(305) 407-9000",
		hoursLabel: "Office Hours:",
		hours: [
			"Monday: 8:30am-5pm",
			"Tuesday: 8:30am-5pm",
			"Wednesday: 8:30am-5pm",
			"Thursday: 8:30am-5pm",
			"Friday: 8:30am-5pm",
		],
		bracesHeading: "Miami Braces",
		bracesParagraphs: [
			"Did you know that traditional braces are one of the most efficient orthodontic tools available? Ceramic or metal brackets are attached to your teeth, then connected by wires and tiny elastics or rubber bands. Through regular adjustments with Dr. Garcia-Paul, these components work together to gently apply pressure, which will gradually move your teeth into the desired positions.",
			"Miami braces are an excellent choice for correcting a number of tooth issues, from simple and complex. Traditional braces can sometimes work faster at closing gaps and aligning teeth than other options might, and this can shorten your overall treatment time. In addition, braces don't require some of the more complicated technology or expensive equipment that newer treatments can. This makes Miami braces a cost-effective orthodontic option for patients on a budget.",
		],
		invisalignHeading: "Miami Invisalign",
		invisalignParagraphs: [
			"The Miami Invisalign system utilizes a series of clear, removable aligners to gradually shift the teeth into alignment instead of using brackets and bands. This option is incredibly popular with both patients and orthodontists making it the top alternative to traditional braces.",
			"Invisalign is most effective when used to correct many of the most common orthodontic issues. For certain more complex cases, Invisalign may not be the best option. A free consultation with Miami Orthodontic Specialists will be able to help determine your eligibility for Invisalign.",
			"Invisalign aligners are made of a comfortable plastic material and designed to fit snugly over the patient's teeth. The trays are nearly invisible and are typically changed out every 1-2 weeks for the next aligner in the series to keep the teeth moving in the right direction! Aligners must be worn 20-22 hours per day throughout treatment in order for them to have the best results. If you don't wear your aligners as directed you might negatively impact your results, and make your orthodontic treatment process longer.",
		],
		treatmentHeading:
			"Miami Orthodontics: find the treatment that fits your smile",
		treatmentParagraphs: [
			"Dr. Garcia-Paul and the skilled staff at Miami Orthodontic Specialists are experts in providing personalized treatment plans for each patient we see. When you visit our office for your free initial consultation, you'll discover that we focus on your specific orthodontic needs, as well as your unique tooth shape, the movement of your lips, and the shape of your face. By using these as the basis for your treatment, we can give you a beautifully aligned smile that is just right for you.",
		],
		finalPrefix:
			"To learn more about Miami orthodontics, call now to schedule your ",
		finalLink: "free consultation",
		finalSuffix: " with Dr. Garcia-Paul.",
	},
	[Locale.ES]: {
		heroTitle: "Ortodoncia en Miami",
		introBeforePractice: "En ",
		practiceLink: "Miami Orthodontic Specialists",
		introAfterPractice:
			", la ortodoncia de alta calidad es nuestra prioridad principal, y nos enorgullece crear sonrisas bellamente alineadas para pacientes de ",
		agesLink: "todas las edades",
		introAfterAges: " en ",
		cityLink: "Miami",
		introAfterCity:
			" y las comunidades cercanas. Miami Orthodontic Specialists es tu experto en ortodoncia en Miami.",
		addressLabel: "Dirección:",
		addressLine1: "8525 SW 92nd Street, Suite B-9",
		addressLine2: "Miami, FL 33156",
		phoneLabel: "Teléfono:",
		phone: "(305) 407-9000",
		hoursLabel: "Horario de atención:",
		hours: [
			"Lunes: 8:30am-5pm",
			"Martes: 8:30am-5pm",
			"Miércoles: 8:30am-5pm",
			"Jueves: 8:30am-5pm",
			"Viernes: 8:30am-5pm",
		],
		bracesHeading: "Brackets en Miami",
		bracesParagraphs: [
			"¿Sabías que los brackets tradicionales son una de las herramientas ortodóncicas más eficientes disponibles? Los brackets cerámicos o metálicos se adhieren a tus dientes y luego se conectan con alambres y pequeñas ligas o bandas de goma. Con ajustes regulares con la Dra. Garcia-Paul, estos componentes trabajan juntos para aplicar presión suavemente, lo que moverá gradualmente tus dientes hacia las posiciones deseadas.",
			"Los brackets en Miami son una excelente opción para corregir una variedad de problemas dentales, desde simples hasta complejos. Los brackets tradicionales a veces pueden cerrar espacios y alinear dientes más rápido que otras opciones, lo que puede acortar el tiempo total de tratamiento. Además, los brackets no requieren algunas de las tecnologías más complejas o equipos costosos que pueden necesitar los tratamientos más nuevos. Esto hace que los brackets en Miami sean una opción ortodóncica rentable para pacientes con presupuesto limitado.",
		],
		invisalignHeading: "Invisalign en Miami",
		invisalignParagraphs: [
			"El sistema Invisalign en Miami utiliza una serie de alineadores transparentes y removibles para mover gradualmente los dientes hacia la alineación, en lugar de usar brackets y bandas. Esta opción es increíblemente popular tanto entre pacientes como ortodoncistas, lo que la convierte en la principal alternativa a los brackets tradicionales.",
			"Invisalign es más eficaz cuando se usa para corregir muchos de los problemas ortodóncicos más comunes. En algunos casos más complejos, Invisalign puede no ser la mejor opción. Una consulta gratuita con Miami Orthodontic Specialists podrá ayudar a determinar si eres candidato para Invisalign.",
			"Los alineadores Invisalign están hechos de un material plástico cómodo y diseñados para ajustarse perfectamente sobre los dientes del paciente. Las bandejas son casi invisibles y normalmente se cambian cada 1 a 2 semanas por el siguiente alineador de la serie para mantener los dientes moviéndose en la dirección correcta. Los alineadores deben usarse de 20 a 22 horas al día durante todo el tratamiento para obtener los mejores resultados. Si no usas tus alineadores según las indicaciones, podrías afectar negativamente tus resultados y hacer que tu proceso de tratamiento ortodóncico sea más largo.",
		],
		treatmentHeading:
			"Ortodoncia en Miami: encuentra el tratamiento que se adapta a tu sonrisa",
		treatmentParagraphs: [
			"La Dra. Garcia-Paul y el equipo experto de Miami Orthodontic Specialists son especialistas en crear planes de tratamiento personalizados para cada paciente que atendemos. Cuando visites nuestra oficina para tu consulta inicial gratuita, descubrirás que nos enfocamos en tus necesidades ortodóncicas específicas, así como en la forma única de tus dientes, el movimiento de tus labios y la forma de tu rostro. Al usar estos aspectos como base para tu tratamiento, podemos darte una sonrisa bellamente alineada y adecuada para ti.",
		],
		finalPrefix:
			"Para obtener más información sobre la ortodoncia en Miami, llama ahora para programar tu ",
		finalLink: "consulta gratuita",
		finalSuffix: " con la Dra. Garcia-Paul.",
	},
} satisfies Record<Locale, MiamiOrthodonticsLocalization>;

export function MiamiOrthodonticsPage() {
	const getLocalization = useLocalization(miamiOrthodonticsLocalization);

	return (
		<Flex direction="column">
			<section className={styles.miamiOrthodonticsHero}>
				<Heading as="h1" className={styles.miamiOrthodonticsHeroTitle}>
					{getLocalization("heroTitle")}
				</Heading>
			</section>
			<article className={styles.miamiOrthodonticsArticle}>
				<Text as="p" className={styles.miamiOrthodonticsCopy}>
					{getLocalization("introBeforePractice")}
					<Link to="/" className={styles.inlineLink}>
						{getLocalization("practiceLink")}
					</Link>
					{getLocalization("introAfterPractice")}
					<Link to="/orthodontics-for-all-ages" className={styles.inlineLink}>
						{getLocalization("agesLink")}
					</Link>
					{getLocalization("introAfterAges")}
					<Link to="/your-miami-orthodontist" className={styles.inlineLink}>
						{getLocalization("cityLink")}
					</Link>
					{getLocalization("introAfterCity")}
				</Text>
				<Grid className={styles.miamiOrthodonticsContactGrid}>
					<Box className={styles.miamiOrthodonticsContact}>
						<Text as="p" className={styles.miamiOrthodonticsCopy}>
							<strong>{getLocalization("addressLabel")}</strong>
							<br />
							{getLocalization("addressLine1")}
							<br />
							{getLocalization("addressLine2")}
						</Text>
						<Text as="p" className={styles.miamiOrthodonticsCopy}>
							<strong>{getLocalization("phoneLabel")}</strong>{" "}
							{getLocalization("phone")}
						</Text>
						<Text as="p" className={styles.miamiOrthodonticsCopy}>
							<strong>{getLocalization("hoursLabel")}</strong>
							<br />
							{getLocalization("hours").map((hour) => (
								<Fragment key={hour}>
									{hour}
									<br />
								</Fragment>
							))}
						</Text>
					</Box>
					<iframe
						allowFullScreen
						className={styles.miamiOrthodonticsMap}
						loading="lazy"
						referrerPolicy="no-referrer-when-downgrade"
						src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3595.594000117996!2d-80.33588008454495!3d25.684750018048977!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x88d9c7107399af23%3A0xaf183ae55f79561b!2sMiami%20Orthodontic%20Specialists%2C%20Dr.%20Carmen%20Garcia-Paul!5e0!3m2!1sen!2sus!4v1679341047815!5m2!1sen!2sus"
						title="Miami Orthodontic Specialists map"
					/>
				</Grid>
				<section className={styles.miamiOrthodonticsSection}>
					<Heading as="h3" className={styles.miamiOrthodonticsHeading}>
						{getLocalization("bracesHeading")}
					</Heading>
					{getLocalization("bracesParagraphs").map((paragraph) => (
						<Text
							as="p"
							className={styles.miamiOrthodonticsCopy}
							key={paragraph}
						>
							{paragraph}
						</Text>
					))}
				</section>
				<section className={styles.miamiOrthodonticsSection}>
					<Heading as="h3" className={styles.miamiOrthodonticsHeading}>
						{getLocalization("invisalignHeading")}
					</Heading>
					{getLocalization("invisalignParagraphs").map((paragraph) => (
						<Text
							as="p"
							className={styles.miamiOrthodonticsCopy}
							key={paragraph}
						>
							{paragraph}
						</Text>
					))}
				</section>
				<section className={styles.miamiOrthodonticsSection}>
					<Heading as="h3" className={styles.miamiOrthodonticsHeading}>
						{getLocalization("treatmentHeading")}
					</Heading>
					{getLocalization("treatmentParagraphs").map((paragraph) => (
						<Text
							as="p"
							className={styles.miamiOrthodonticsCopy}
							key={paragraph}
						>
							{paragraph}
						</Text>
					))}
					<Text as="p" className={styles.miamiOrthodonticsCopy}>
						{getLocalization("finalPrefix")}
						<Link to="/free-consult" className={styles.inlineLink}>
							{getLocalization("finalLink")}
						</Link>
						{getLocalization("finalSuffix")}
					</Text>
				</section>
			</article>
			<Box className={styles.formWrap}>
				<HomeConsultForm />
			</Box>
		</Flex>
	);
}
